import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { User as SupabaseUser, Session } from "@supabase/supabase-js";
import {
  supabase,
  User,
  Pharmacy,
  UserPharmacy,
  Tables,
  AuthErrorType,
} from "../utils/supabase";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  pharmacies: Pharmacy[];
  selectedPharmacy: Pharmacy | null;
  userPharmacies: UserPharmacy[];
  loading: boolean;
  needsEmailVerification: boolean;
  isQuickSwitched: boolean;
  originalUser: User | null;
  switchHistory: User[];
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ error: AuthErrorType }>;
  signUp: (
    email: string,
    password: string,
    fullName: string,
  ) => Promise<{ error: AuthErrorType }>;
  signOut: () => Promise<void>;
  selectPharmacy: (pharmacy: Pharmacy) => void;
  refreshUserData: () => Promise<void>;
  resendVerification: () => Promise<{ error: AuthErrorType }>;
  handleEmailConfirmation: () => Promise<void>;
  quickSwitchUser: (
    userId: string,
  ) => Promise<{ error: AuthErrorType | null; user?: User }>;
  switchBackToOriginal: () => Promise<void>;
  clearSwitchHistory: () => void;
  forceRefresh: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState<Pharmacy | null>(
    null,
  );
  const [userPharmacies, setUserPharmacies] = useState<UserPharmacy[]>([]);
  const [loading, setLoading] = useState(true);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [isQuickSwitched, setIsQuickSwitched] = useState(false);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [switchHistory, setSwitchHistory] = useState<User[]>([]);
  const _isRefreshing = useRef(false);
  const refreshTimeout = useRef<NodeJS.Timeout | null>(null);
  const loadingTimeout = useRef<NodeJS.Timeout | null>(null);
  const isResetting = useRef(false);
  const lastFetchedUserId = useRef<string | null>(null);
  const authStateChangeDebounce = useRef<NodeJS.Timeout | null>(null);

  // Manual app reset - only for user-initiated reset
  const completeAppReset = async () => {
    if (isResetting.current) {
      return;
    }

    console.log("Performing manual app reset");
    isResetting.current = true;

    // Clear all timeouts
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
      refreshTimeout.current = null;
    }
    if (loadingTimeout.current) {
      clearTimeout(loadingTimeout.current);
      loadingTimeout.current = null;
    }

    // Clear all storage and caches
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();

      // Clear localStorage
      localStorage.clear();

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear service worker caches
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName)),
        );
      }

      // Unregister service workers
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          registration.unregister();
        }
      }
    } catch (error) {
      console.error("Error during reset:", error);
    }

    // Force page reload to ensure clean state
    window.location.reload();
  };

  // No automatic timeouts or loading management

  useEffect(() => {
    // Clear all potentially corrupted cached data on startup
    console.log("AuthContext initializing...");

    // Don't clear auth data on page refresh - it breaks session persistence
    // Only clear non-auth related stale data
    const clearNonAuthData = async () => {
      try {
        // Clear only non-auth sessionStorage
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key && !key.includes("supabase") && !key.includes("auth")) {
            sessionStorage.removeItem(key);
          }
        }
        console.log("Cleared non-auth session data");
      } catch (error) {
        console.warn("Error clearing non-auth data:", error);
      }
    };

    clearNonAuthData();

    // Test Supabase connectivity first
    console.log("Testing Supabase connectivity...");
    (async () => {
      try {
        const connectivityTimeout = new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("Connectivity test timeout")),
            5000,
          ),
        );
        const connectivityTest = supabase.from("users").select("id").limit(1);

        const result = await Promise.race([
          connectivityTest,
          connectivityTimeout,
        ]);
        console.log("Supabase connectivity test result:", result);
      } catch (error) {
        console.error("Supabase connectivity test failed:", error);
        console.error("This likely means Supabase is not reachable");

        // If connectivity fails, try to clear Supabase client cache
        try {
          await supabase.auth.signOut();
          console.log("Cleared Supabase auth session");
        } catch (e) {
          console.warn("Could not clear auth session:", e);
        }
      }
    })();

    // Get initial session with timeout and retry logic
    console.log("Getting initial session...");

    const sessionTimeout = setTimeout(() => {
      console.error("Session fetch timeout after 15 seconds");
      setLoading(false);
    }, 15000);

    // First try to refresh the session to ensure we have valid tokens
    supabase.auth
      .refreshSession()
      .then(({ data: { session: _session }, error }) => {
        if (error) {
          console.warn("Session refresh failed:", error);

          // Check if it's a refresh token error
          if (
            error.message?.includes("Refresh Token") ||
            error.message?.includes("refresh_token") ||
            error.status === 400
          ) {
            console.error(
              "Refresh token is invalid, clearing auth and redirecting to login",
            );

            // Clear all auth data
            localStorage.removeItem("supabase.auth.token");
            const authKeys = Object.keys(localStorage).filter(
              (key) => key.includes("supabase") || key.includes("auth"),
            );
            authKeys.forEach((key) => localStorage.removeItem(key));

            // Sign out to clear server state
            return supabase.auth.signOut().then(() => {
              // Return null session to trigger login
              return { data: { session: null }, error: null };
            });
          }

          // For other errors, fallback to getSession
          return supabase.auth.getSession();
        }
        console.log("Session refreshed successfully");
        return supabase.auth.getSession(); // Return consistent type
      })
      .then(({ data: { session } }) => {
        clearTimeout(sessionTimeout);
        console.log(
          "Initial session result:",
          session?.user?.email || "no session",
        );
        setSession(session);
        if (session?.user) {
          console.log("Initial session: User found, fetching profile...");

          // Add small delay to ensure auth headers are properly set
          setTimeout(() => {
            fetchUserProfile(session.user);
          }, 100);
        } else {
          console.log("Initial session: No user, stopping loading");
          setLoading(false);
        }
      })
      .catch((error) => {
        clearTimeout(sessionTimeout);
        console.error("Error getting session:", error);
        setLoading(false);
      });

    // Listen for auth changes - minimal handling with debounce
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log(
        "Auth state change:",
        event,
        "session:",
        session?.user?.email,
      );

      // Clear any pending debounce
      if (authStateChangeDebounce.current) {
        clearTimeout(authStateChangeDebounce.current);
      }

      // Debounce auth state changes to prevent rapid triggers
      authStateChangeDebounce.current = setTimeout(async () => {
        // Only handle certain events to prevent loops
        if (event === "INITIAL_SESSION" || event === "USER_UPDATED") {
          console.log("Ignoring event:", event);
          return;
        }

        // Prevent re-fetching if we already have this user's data
        if (
          event === "SIGNED_IN" &&
          session?.user &&
          user?.id === session.user.id
        ) {
          console.log("User already loaded, skipping re-fetch");
          return;
        }

        // Handle token refresh errors
        if (event === "TOKEN_REFRESHED" && !session) {
          console.error("Token refresh failed - invalid refresh token");
          // Clear auth and force re-login
          localStorage.removeItem("supabase.auth.token");
          const authKeys = Object.keys(localStorage).filter(
            (key) => key.includes("supabase") || key.includes("auth"),
          );
          authKeys.forEach((key) => localStorage.removeItem(key));
          setLoading(false);
          return;
        }

        setSession(session);

        // Handle authentication state changes
        if (session?.user) {
          console.log(
            "Auth state change: User found, checking email verification...",
          );
          // Check if email is verified
          if (session.user.email_confirmed_at) {
            console.log(
              "Auth state change: Email verified, fetching profile...",
            );
            setNeedsEmailVerification(false);
            await fetchUserProfile(session.user);
          } else {
            console.log("Auth state change: Email not verified");
            setNeedsEmailVerification(true);
            setLoading(false);
          }
        } else {
          console.log("Auth state change: No session, clearing state");
          // No session - clear all state
          setUser(null);
          setPharmacies([]);
          setSelectedPharmacy(null);
          setUserPharmacies([]);
          setNeedsEmailVerification(false);
          setLoading(false);
        }
      }, 500); // 500ms debounce
    });

    // Cleanup function
    return () => {
      clearTimeout(sessionTimeout);
      if (authStateChangeDebounce.current) {
        clearTimeout(authStateChangeDebounce.current);
      }
      subscription.unsubscribe();
    };
  }, []);

  const processUserInvitation = async (supabaseUser: SupabaseUser) => {
    console.log("processUserInvitation: Starting for", supabaseUser.email);
    try {
      // Check for pending invitations for this email
      console.log("processUserInvitation: Checking for invitations...");
      const { data: invitation, error: invitationError } = await supabase
        .from("user_invitations")
        .select("*")
        .eq("email", supabaseUser.email)
        .eq("status", "pending")
        .gt("expires_at", new Date().toISOString())
        .single();

      console.log("processUserInvitation: Invitation query result:", {
        invitation,
        invitationError,
      });

      if (invitationError) {
        // If table doesn't exist or no invitation found, just return
        if (invitationError.code === "42P01") {
          console.log(
            "processUserInvitation: user_invitations table doesn't exist",
          );
        }
        return null;
      }

      if (!invitation) {
        console.log("processUserInvitation: No pending invitation found");
        return null;
      }

      console.log("Processing pending invitation for", supabaseUser.email);

      // Create user profile based on invitation
      const { error: profileError } = await supabase.from(Tables.USERS).insert({
        id: supabaseUser.id,
        email: invitation.email,
        full_name: invitation.full_name,
        role: invitation.role,
      });

      if (profileError) {
        console.error(
          "Error creating user profile from invitation:",
          profileError,
        );
        return null;
      }

      // Assign user to pharmacies from invitation
      const userPharmacyInserts = invitation.pharmacy_ids.map(
        (pharmacyId: string) => ({
          user_id: supabaseUser.id,
          pharmacy_id: pharmacyId,
          role: invitation.pharmacy_role,
        }),
      );

      const { error: assignmentError } = await supabase
        .from(Tables.USER_PHARMACIES)
        .insert(userPharmacyInserts);

      if (assignmentError) {
        console.error(
          "Error assigning user to pharmacies from invitation:",
          assignmentError,
        );
        return null;
      }

      // Mark invitation as accepted
      await supabase
        .from("user_invitations")
        .update({ status: "accepted" })
        .eq("id", invitation.id);

      console.log(
        "processUserInvitation: Successfully processed invitation for",
        supabaseUser.email,
      );
      return invitation;
    } catch (error) {
      console.error("processUserInvitation: Error caught:", error);
      // Don't let this error block user creation
      return null;
    }
  };

  const fetchUserProfile = async (supabaseUser: SupabaseUser) => {
    if (isResetting.current) {
      return;
    }

    // Skip if we already fetched this user's profile
    if (
      lastFetchedUserId.current === supabaseUser.id &&
      user?.id === supabaseUser.id
    ) {
      console.log("fetchUserProfile: Already fetched for this user, skipping");
      setLoading(false);
      return;
    }

    // Add debugging timeout to force completion
    const debugTimeout = setTimeout(() => {
      console.error(
        "fetchUserProfile: TIMEOUT - Force completing after 30 seconds",
      );
      setLoading(false);
    }, 30000);

    try {
      console.log("fetchUserProfile: Starting for user:", supabaseUser.id);
      setLoading(true);
      lastFetchedUserId.current = supabaseUser.id;

      // Fetch user profile
      console.log("fetchUserProfile: Fetching user profile from database...");
      console.log("Using table name:", Tables.USERS);

      // Test with direct table name first
      // Skip the direct query test - it's causing issues
      console.log("fetchUserProfile: Skipping direct query test");

      // Add timeout to prevent hanging - with retry logic
      const fetchWithRetry = async (retries = 2) => {
        for (let i = 0; i <= retries; i++) {
          try {
            if (i > 0) {
              console.log(`Retry ${i} for user profile fetch...`);
              // Wait before retry
              await new Promise((resolve) => setTimeout(resolve, 1000));
            }

            const result = await supabase
              .from(Tables.USERS)
              .select("*")
              .eq("id", supabaseUser.id)
              .single();

            if (result.error) {
              throw result.error;
            }
            return result;
          } catch (error) {
            if (i === retries) {
              throw error;
            }
          }
        }
      };

      const userProfilePromise = fetchWithRetry();

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("User profile fetch timeout")),
          15000,
        ),
      );

      let userData, userError;
      try {
        const result = (await Promise.race([
          userProfilePromise,
          timeoutPromise,
        ])) as { data: any; error: any };
        userData = result.data;
        userError = result.error;
      } catch (error) {
        userData = null;
        userError = error;
      }

      console.log("fetchUserProfile: User profile fetch result:", {
        userData,
        userError,
      });

      if (userError) {
        console.log("fetchUserProfile: User error detected:", userError);
        // If user doesn't exist in our users table, create one
        if (userError.code === "PGRST116") {
          console.log(
            "fetchUserProfile: User not found, creating new user profile...",
          );
          // Check for pending invitations first (but don't block on it)
          try {
            await processUserInvitation(supabaseUser);
          } catch (e) {
            console.warn(
              "processUserInvitation failed, continuing without it:",
              e,
            );
          }

          const { data: newUser, error: createError } = await supabase
            .from(Tables.USERS)
            .insert({
              id: supabaseUser.id,
              email: supabaseUser.email || "",
              full_name: supabaseUser.user_metadata?.full_name || "",
              role: "employee",
            })
            .select()
            .single();

          console.log("fetchUserProfile: User creation result:", {
            newUser,
            createError,
          });

          if (createError) {
            console.error("Error creating user profile:", createError);
            setLoading(false);
            return;
          }
          setUser(newUser);
        } else {
          console.error("Error fetching user profile:", userError);
          setLoading(false);
          return;
        }
      } else {
        console.log("fetchUserProfile: Setting user data:", userData);
        setUser(userData);
      }

      // Fetch user's pharmacies with separate queries
      console.log("fetchUserProfile: Fetching user pharmacies...");

      const userPharmaciesPromise = supabase
        .from(Tables.USER_PHARMACIES)
        .select("*")
        .eq("user_id", supabaseUser.id);

      const pharmaciesTimeoutPromise = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("User pharmacies fetch timeout")),
          10000,
        ),
      );

      let userPharmaciesData, userPharmaciesError;
      try {
        const result = (await Promise.race([
          userPharmaciesPromise,
          pharmaciesTimeoutPromise,
        ])) as { data: any; error: any };
        userPharmaciesData = result.data;
        userPharmaciesError = result.error;
      } catch (error) {
        userPharmaciesData = null;
        userPharmaciesError = error;
      }

      console.log("fetchUserProfile: User pharmacies result:", {
        userPharmaciesData,
        userPharmaciesError,
      });

      if (userPharmaciesError) {
        console.error("Error fetching user pharmacies:", userPharmaciesError);
        setLoading(false);
        return;
      }

      setUserPharmacies(userPharmaciesData || []);

      // Get pharmacy IDs and fetch pharmacy details separately
      const pharmacyIds =
        userPharmaciesData?.map((up: UserPharmacy) => up.pharmacy_id) || [];
      console.log("fetchUserProfile: Pharmacy IDs to fetch:", pharmacyIds);

      let pharmaciesData: Pharmacy[] = [];

      if (pharmacyIds.length > 0) {
        console.log("fetchUserProfile: Fetching pharmacy details...");

        const pharmacyDetailsPromise = supabase
          .from(Tables.PHARMACIES)
          .select("*")
          .in("id", pharmacyIds);

        const detailsTimeoutPromise = new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("Pharmacy details fetch timeout")),
            10000,
          ),
        );

        let fetchedPharmacies, pharmaciesError;
        try {
          const result = (await Promise.race([
            pharmacyDetailsPromise,
            detailsTimeoutPromise,
          ])) as { data: any; error: any };
          fetchedPharmacies = result.data;
          pharmaciesError = result.error;
        } catch (error) {
          fetchedPharmacies = null;
          pharmaciesError = error;
        }

        console.log("fetchUserProfile: Pharmacies fetch result:", {
          fetchedPharmacies,
          pharmaciesError,
        });

        if (pharmaciesError) {
          console.error("Error fetching pharmacies:", pharmaciesError);
          setLoading(false);
          return;
        }

        pharmaciesData = fetchedPharmacies || [];

        setPharmacies(pharmaciesData);
      } else {
        console.log(
          "fetchUserProfile: No pharmacies to fetch, setting empty array",
        );
        setPharmacies([]);
      }

      // Set first pharmacy as selected if none is selected
      if (pharmaciesData.length > 0 && !selectedPharmacy) {
        console.log(
          "fetchUserProfile: Setting first pharmacy as selected:",
          pharmaciesData[0],
        );
        setSelectedPharmacy(pharmaciesData[0]);
      } else {
        console.log(
          "fetchUserProfile: Not setting pharmacy - either no pharmacies or already selected",
        );
      }

      console.log("fetchUserProfile: Completed successfully");
    } catch (error) {
      console.error("Error in fetchUserProfile:", error);
      console.log("fetchUserProfile: Failed with error, stopping loading");
    } finally {
      // Clear the debug timeout
      clearTimeout(debugTimeout);

      console.log(
        "fetchUserProfile: In finally block, isResetting:",
        isResetting.current,
      );
      if (!isResetting.current) {
        console.log("fetchUserProfile: Setting loading to false");
        setLoading(false);
      } else {
        console.log("fetchUserProfile: Skipping loading=false due to reset");
      }
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        console.error("Sign in error:", error);
        setLoading(false);
        return { error };
      }
      // fetchUserProfile will be called by the auth state change listener
      return { error: null };
    } catch (err) {
      console.error("Sign in network error:", err);
      setLoading(false);
      return {
        error: {
          message:
            "Network error. Please check your connection and Supabase configuration.",
        },
      };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        console.error("Sign up error:", error);
        setLoading(false);
        return { error };
      }

      setNeedsEmailVerification(true);
      setLoading(false);
      return { error: null };
    } catch (err) {
      console.error("Sign up network error:", err);
      setLoading(false);
      return {
        error: {
          message:
            "Network error. Please check your connection and Supabase configuration.",
        },
      };
    }
  };

  const signOut = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setUser(null);
    setPharmacies([]);
    setSelectedPharmacy(null);
    setUserPharmacies([]);
    setNeedsEmailVerification(false);
    setIsQuickSwitched(false);
    setOriginalUser(null);
    setSwitchHistory([]);
    lastFetchedUserId.current = null;
    setLoading(false);
  };

  const selectPharmacy = (pharmacy: Pharmacy) => {
    setSelectedPharmacy(pharmacy);
    // Store in localStorage for persistence
    localStorage.setItem("selectedPharmacyId", pharmacy.id);
  };

  const refreshUserData = async () => {
    if (session?.user) {
      await fetchUserProfile(session.user);
    }
  };

  const quickSwitchUser = async (userId: string) => {
    setLoading(true);
    try {
      // Fetch the target user's data
      const { data: targetUser, error: userError } = await supabase
        .from(Tables.USERS)
        .select("*")
        .eq("id", userId)
        .single();

      if (userError || !targetUser) {
        console.error("Error fetching target user:", userError);
        setLoading(false);
        return { error: { message: "User not found" } };
      }

      // Store original user if this is the first switch
      if (!isQuickSwitched && user) {
        setOriginalUser(user);
        // Add current user to switch history
        setSwitchHistory([user]);
      } else if (user) {
        // Add current user to switch history if continuing to switch
        const newHistory = [...switchHistory, user];
        setSwitchHistory(newHistory);
      }

      // Set the new user
      setUser(targetUser);
      setIsQuickSwitched(true);

      // Fetch user's pharmacies with separate queries
      const { data: userPharmaciesData, error: userPharmaciesError } =
        await supabase
          .from(Tables.USER_PHARMACIES)
          .select("*")
          .eq("user_id", userId);

      if (userPharmaciesError) {
        console.error("Error fetching user pharmacies:", userPharmaciesError);
        setLoading(false);
        return { error: { message: "Error loading user data" } };
      }

      setUserPharmacies(userPharmaciesData || []);

      // Get pharmacy IDs and fetch pharmacy details separately
      const pharmacyIds = userPharmaciesData?.map((up) => up.pharmacy_id) || [];

      let pharmaciesData: Pharmacy[] = [];

      if (pharmacyIds.length > 0) {
        const { data: fetchedPharmacies, error: pharmaciesError } =
          await supabase
            .from(Tables.PHARMACIES)
            .select("*")
            .in("id", pharmacyIds);

        if (pharmaciesError) {
          console.error("Error fetching pharmacies:", pharmaciesError);
          setLoading(false);
          return { error: { message: "Error loading pharmacy data" } };
        }

        pharmaciesData = fetchedPharmacies || [];
        setPharmacies(pharmaciesData);
      } else {
        setPharmacies([]);
      }

      // Set first pharmacy as selected if none is selected
      if (pharmaciesData.length > 0) {
        setSelectedPharmacy(pharmaciesData[0]);
      } else {
        setSelectedPharmacy(null);
      }

      setLoading(false);
      return { error: null, user: targetUser };
    } catch (error) {
      console.error("Error in quickSwitchUser:", error);
      setLoading(false);
      return {
        error: { message: "An error occurred during user switch" },
        user: undefined,
      };
    }
  };

  const switchBackToOriginal = async () => {
    if (!originalUser) {
      return;
    }

    setLoading(true);
    try {
      // Restore original user
      setUser(originalUser);
      setIsQuickSwitched(false);
      setOriginalUser(null);
      setSwitchHistory([]);

      // Refresh user data for original user
      if (session?.user) {
        await fetchUserProfile(session.user);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error switching back to original user:", error);
      setLoading(false);
    }
  };

  const clearSwitchHistory = () => {
    setIsQuickSwitched(false);
    setOriginalUser(null);
    setSwitchHistory([]);
  };

  const resendVerification = async () => {
    if (!session?.user?.email) {
      return { error: { message: "No email found" } };
    }

    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: session.user.email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        console.error("Resend verification error:", error);
      }

      return { error };
    } catch (err) {
      console.error("Resend verification network error:", err);
      return {
        error: {
          message:
            "Network error. Please check your connection and Supabase configuration.",
        },
      };
    }
  };

  const handleEmailConfirmation = async () => {
    setLoading(true);

    try {
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();

      if (currentSession?.user?.email_confirmed_at) {
        setNeedsEmailVerification(false);
        await fetchUserProfile(currentSession.user);
      }
    } catch (error) {
      console.error("Error handling email confirmation:", error);
    } finally {
      setLoading(false);
    }
  };

  // Restore selected pharmacy from localStorage
  useEffect(() => {
    if (pharmacies.length > 0) {
      const savedPharmacyId = localStorage.getItem("selectedPharmacyId");
      if (savedPharmacyId) {
        const savedPharmacy = pharmacies.find((p) => p.id === savedPharmacyId);
        if (savedPharmacy) {
          setSelectedPharmacy(savedPharmacy);
        }
      }
    }
  }, [pharmacies]);

  // No session storage restoration - keep state only during session

  const value: AuthContextType = {
    user,
    session,
    pharmacies,
    selectedPharmacy,
    userPharmacies,
    loading,
    needsEmailVerification,
    isQuickSwitched,
    originalUser,
    switchHistory,
    signIn,
    signUp,
    signOut,
    selectPharmacy,
    refreshUserData,
    resendVerification,
    handleEmailConfirmation,
    quickSwitchUser,
    switchBackToOriginal,
    clearSwitchHistory,
    forceRefresh: completeAppReset,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
